import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { Icon } from './Icon.js';
import { cx } from './utils.js';

export function Modal(props: {
  title: string;
  description?: string | undefined;
  wide?: boolean | undefined;
  onClose: () => void;
  children: ReactNode;
}) {
  const { text } = useAdminLocale();

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') props.onClose();
    }
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [props.onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
      <div
        className="absolute inset-0 bg-[color:var(--admin-ink,#0b1220)]/55 backdrop-blur-[1px]"
        onClick={props.onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-modal-title"
        className={cx(
          'admin-auth-enter relative z-10 max-h-[calc(100dvh-2rem)] w-full overflow-y-auto rounded-[var(--admin-radius,0.625rem)] border border-[color:var(--admin-border,#d8dee6)] bg-[color:var(--admin-surface,#fff)] shadow-[var(--admin-shadow-md)] dark:border-slate-700 dark:bg-slate-900',
          props.wide ? 'max-w-2xl' : 'max-w-xl',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-[color:var(--admin-border)] px-5 py-3.5 dark:border-slate-800">
          <div>
            <h3
              id="app-modal-title"
              className="admin-display text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              {props.title}
            </h3>
            {props.description ? (
              <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                {props.description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={props.onClose}
            aria-label={text('Κλείσιμο', 'Close')}
            className="grid size-8 shrink-0 place-items-center rounded-[var(--admin-radius-sm)] text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--admin-accent)]/40 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          >
            <Icon name="x" className="size-4" />
          </button>
        </header>
        <div className="p-5">{props.children}</div>
      </div>
    </div>,
    document.body,
  );
}
